"use client";

import React from "react";
import { CircleCheck, Loader, Zap } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const features = ["Local-first architecture with end-to-end encryption", "Native iMessage command & control workflows", "Granular Identity, Soul, and Memory modules", "Dedicated Mac mini or managed multi-tenant hosting", "Secure approval layers for high-stakes operations", "Custom operator deployments with full ownership"];
const deploymentTypes = ["Dedicated Mac mini (On-Prem)", "Managed Multi-tenant (qOS Cloud)", "Custom Operator Deployment", "Enterprise Private Cluster", "Developer Sandbox"];
const instanceTiers = ["Standard Operator – $299/mo", "Professional Node – $899/mo", "Custom Enterprise – Contact Sales"];
function MembershipFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="117912" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4 border border-primary/30">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-sans">Request Logged</h3>
        <p className="text-muted-foreground font-mono text-sm">{successMessage}</p>
      </div>;
  }
  const inputBaseClasses = "flex w-full rounded-md border border-border bg-secondary/20 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all font-mono";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormField control={control} name="full_name" rules={{
        required: "Operator Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Operator Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="ID_ALPHA_01" className={inputBaseClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] text-muted-foreground font-mono">
                Primary identifier for system logs and audit trails.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "System Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid system email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">System Email</FormLabel>
              <FormControl>
                <input type="email" placeholder="operator@quotient.technology" className={inputBaseClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid contact number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Emergency Callback <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <input type="tel" placeholder="+1 (555) 000-0000" className={inputBaseClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] text-muted-foreground font-mono">
                Required for high-priority hardware alerts and failover notifications.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fitness_goal" rules={{
        required: "Select a deployment environment"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Deployment Environment</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={`${inputBaseClasses} appearance-none cursor-pointer`} {...field}>
                    <option value="" disabled>
                      Select configuration
                    </option>
                    {deploymentTypes.map((goal, i) => <option key={i} data-index={i} value={goal} className="bg-card text-foreground">
                        {goal}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="membership_plan" rules={{
        required: "Select an access tier"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Access Configuration</FormLabel>
              <FormDescription className="text-[10px] text-muted-foreground font-mono mb-2">
                Provisioning level for your qOS instance.
              </FormDescription>
              <FormControl>
                <div className="space-y-2">
                  {instanceTiers.map((plan, i) => <label key={i} data-index={i} className={`flex items-center space-x-3 cursor-pointer p-4 rounded-md border transition-all ${field.value === plan ? "border-primary bg-primary/5" : "border-border bg-secondary/10 hover:bg-secondary/20"}`}>
                      <input type="radio" className="sr-only" {...field} value={plan} checked={field.value === plan} />
                      <div className={`size-4 rounded-full border flex items-center justify-center ${field.value === plan ? 'border-primary' : 'border-muted-foreground'}`}>
                        {field.value === plan && <div className="size-2 rounded-full bg-primary" />}
                      </div>
                      <span className="text-xs font-mono font-medium text-foreground">{plan}</span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-xs font-mono">
          SYSTEM_ERROR: {submitError}
        </div>}

      <div className="space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white text-sm font-bold tracking-widest uppercase shadow-lg shadow-blue-500/10 transition-all hover:scale-[1.01] active:scale-[0.99] border-t border-white/10">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              PROVISIONING...
            </> : "REQUEST SYSTEM ACCESS"}
        </Button>
        <p className="text-[10px] text-center text-muted-foreground font-mono uppercase tracking-tight opacity-60">
          Local-first data privacy. No third-party data collection.
        </p>
      </div>
    </Form>;
}
export default function InitiateMembership() {
  return <section id="secure-access" className="dark bg-background text-foreground py-24 lg:py-32 px-6 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none opacity-40" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center relative z-10">
        
        {}
        <div className="space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-[10px] font-mono font-bold tracking-[0.2em] uppercase mb-4">
            <Zap className="size-3" />
            Final Deployment
          </div>
          
          <h2 className="text-5xl lg:text-7xl font-bold tracking-tighter text-foreground uppercase leading-[1.1]">
            Initialize Your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-violet-500">Deployment</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-md font-sans leading-relaxed">
            Provision your private agent environment. Secure, local-first, and native to your existing iMessage workflows.
          </p>

          <div className="space-y-5 pt-4">
            {features.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                <div className="mt-1 bg-primary/10 p-1.5 rounded border border-primary/20 shrink-0">
                  <CircleCheck className="size-4 text-primary" />
                </div>
                <p className="text-foreground/90 font-mono text-sm leading-relaxed">{benefit}</p>
              </div>)}
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute -inset-0.5 bg-gradient-to-b from-primary/30 to-transparent rounded-xl blur-sm opacity-30" />
          
          <div className="relative bg-card/60 rounded-xl p-8 lg:p-12 shadow-2xl border border-border/40 backdrop-blur-xl">
            <div className="mb-10">
              <h3 className="text-xl font-bold text-foreground mb-2 font-sans tracking-tight">System Access Request</h3>
              <p className="text-muted-foreground text-xs font-mono uppercase tracking-wider">Secure your instance and begin onboarding.</p>
            </div>

            <FormProvider formKey="secure_access_form_qos_by_quotient" formId="secure_access_form_qos_by_quotient" sectionName="initiate-membership" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            fitness_goal: "",
            membership_plan: ""
          }} submitText="REQUEST SYSTEM ACCESS" successMessage="Provisioning sequence initiated. Our team will contact you via secure channel shortly.">
              <MembershipFormInner />
            </FormProvider>
          </div>
        </div>

      </div>
    </section>;
}